import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, BarChart3, Settings, User, ChevronRight, Loader2, 
  BookOpen, Trophy, Flame, Star, Bell, LogOut, Play, Plus,
  Calendar, FileText, Shield, Heart, Target, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MobileParentApp = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('home');
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childProgress, setChildProgress] = useState(null);
  const [dailyTasks, setDailyTasks] = useState([]);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeklyStats, setWeeklyStats] = useState(null);

  const api = axios.create({
    baseURL: API_URL,
    headers: { Authorization: `Bearer ${token}` }
  });

  // Quick action items for parents
  const quickActions = [
    { id: 'progress', name: 'Progress Report', icon: FileText, color: 'bg-blue-500', route: (childId) => `/progress-report/${childId}` },
    { id: 'student', name: 'Student View', icon: Play, color: 'bg-emerald-500', route: () => '/mobile/student' },
    { id: 'story', name: 'Story Time', icon: Heart, color: 'bg-pink-500', route: (childId) => `/family-story-time/${childId}` },
    { id: 'badges', name: 'Badges', icon: Award, color: 'bg-amber-500', route: (childId) => `/badges/${childId}` },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchChildData(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchData = async () => {
    try {
      // Fetch children from the dedicated endpoint
      const childrenRes = await api.get('/api/children');
      const childrenData = childrenRes.data || [];
      setChildren(childrenData);
      if (childrenData.length > 0) {
        setSelectedChild(childrenData[0]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChildData = async (childId) => {
    try {
      const [progressRes, tasksRes, streakRes] = await Promise.all([
        api.get(`/api/children/${childId}/progress`),
        api.get(`/api/children/${childId}/daily-tasks`),
        api.get(`/api/children/${childId}/streak`)
      ]);
      setChildProgress(progressRes.data);
      setDailyTasks(tasksRes.data.tasks || []);
      setStreak(streakRes.data);
    } catch (error) {
      console.error('Failed to fetch child data:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const completedTasks = dailyTasks.filter(t => t.completed).length;
  const totalTasks = dailyTasks.length;
  const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 pb-6 rounded-b-3xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold">Welcome, {user?.name?.split(' ')[0] || 'Parent'}!</h1>
            <p className="text-emerald-100 text-sm">Track your children's progress</p>
          </div>
          <button 
            onClick={handleLogout}
            className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Child Selector */}
        {children.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {children.map((child) => (
              <button
                key={child.id}
                onClick={() => setSelectedChild(child)}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                  selectedChild?.id === child.id
                    ? 'bg-white text-emerald-600 font-medium'
                    : 'bg-white/20 text-white'
                }`}
              >
                {child.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'home' && selectedChild && (
          <>
            {/* Child Overview Card */}
            <Card className="mb-4 overflow-hidden shadow-lg" data-testid="child-overview-card">
              <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 p-4 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                    {selectedChild.name?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold">{selectedChild.name}</h2>
                    <p className="text-emerald-100 text-sm">Level {selectedChild.current_level || 1} Learner</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                    onClick={() => navigate(`/learn/${selectedChild.id}`)}
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Learn
                  </Button>
                </div>
              </div>
              <CardContent className="p-3">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <div className="text-xl mb-0.5">⭐</div>
                    <div className="text-sm font-bold text-slate-800">{selectedChild.total_xp || 0}</div>
                    <div className="text-xs text-slate-500">XP</div>
                  </div>
                  <div>
                    <div className="text-xl mb-0.5">🔥</div>
                    <div className="text-sm font-bold text-slate-800">{streak?.current_streak || 0}</div>
                    <div className="text-xs text-slate-500">Streak</div>
                  </div>
                  <div>
                    <div className="text-xl mb-0.5">📚</div>
                    <div className="text-sm font-bold text-slate-800">{childProgress?.lessons_completed || 0}</div>
                    <div className="text-xs text-slate-500">Lessons</div>
                  </div>
                  <div>
                    <div className="text-xl mb-0.5">🏆</div>
                    <div className="text-sm font-bold text-slate-800">{selectedChild.badges?.length || 0}</div>
                    <div className="text-xs text-slate-500">Badges</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Today's Progress */}
            <Card className="mb-4">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>Today's Progress</span>
                  {completedTasks === totalTasks && totalTasks > 0 && (
                    <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">All Done! 🎉</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-500">{completedTasks} of {totalTasks} tasks</span>
                  <span className="text-xs font-bold text-emerald-600">{progressPercent}%</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="mb-4">
              <h3 className="text-sm font-bold text-slate-800 mb-2">⚡ Quick Actions</h3>
              <div className="grid grid-cols-4 gap-2">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => navigate(typeof action.route === 'function' ? action.route(selectedChild.id) : action.route)}
                    className={`${action.color} rounded-xl p-2.5 text-white flex flex-col items-center gap-1 hover:opacity-90 active:scale-95 transition-all`}
                    data-testid={`quick-action-${action.id}`}
                  >
                    <action.icon className="w-5 h-5" />
                    <span className="text-xs font-medium text-center leading-tight">{action.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Daily Tasks */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-slate-800">📋 Daily Tasks</h3>
                <span className="text-xs text-slate-500">{completedTasks}/{totalTasks}</span>
              </div>
              {dailyTasks.length === 0 ? (
                <Card>
                  <CardContent className="p-4 text-center text-slate-500 text-sm">
                    No tasks assigned yet
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {dailyTasks.slice(0, 4).map((task) => (
                    <Card key={task.id} className={task.completed ? 'bg-emerald-50' : ''}>
                      <CardContent className="p-2.5 flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                          task.completed ? 'bg-emerald-500 text-white' : 'border-2 border-slate-300'
                        }`}>
                          {task.completed && '✓'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${task.completed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                            {task.title || `${task.subject} Task`}
                          </p>
                        </div>
                        <span className="text-xs text-amber-500 font-medium">+{task.xp_reward} XP</span>
                      </CardContent>
                    </Card>
                  ))}
                  {dailyTasks.length > 4 && (
                    <button 
                      className="w-full text-center text-xs text-emerald-600 py-2"
                      onClick={() => navigate(`/learn/${selectedChild.id}`)}
                    >
                      View all {dailyTasks.length} tasks →
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Subject Progress Mini */}
            <div>
              <h3 className="text-sm font-bold text-slate-800 mb-2">📊 Subject Progress</h3>
              <Card>
                <CardContent className="p-3 space-y-3">
                  {['Reading', 'Math', 'Writing', 'Science'].map((subject) => {
                    const subjectData = childProgress?.subjects?.[subject.toLowerCase()] || { completed: 0, total: 10 };
                    const percent = Math.round((subjectData.completed / Math.max(subjectData.total, 1)) * 100);
                    const icons = { Reading: '📚', Math: '🔢', Writing: '✏️', Science: '🔬' };
                    
                    return (
                      <div key={subject} className="flex items-center gap-2">
                        <span className="text-lg">{icons[subject]}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-medium text-slate-700">{subject}</span>
                            <span className="text-slate-500">{percent}%</span>
                          </div>
                          <Progress value={percent} className="h-1.5" />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* No children state */}
        {activeTab === 'home' && !selectedChild && !loading && (
          <Card className="text-center py-8">
            <CardContent>
              <div className="text-5xl mb-4">👶</div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">Add Your First Child</h3>
              <p className="text-slate-500 mb-4 text-sm">Get started by adding a child to begin their learning journey</p>
              <Button onClick={() => navigate('/child/new')} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Child
              </Button>
            </CardContent>
          </Card>
        )}

        {activeTab === 'progress' && selectedChild && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-800">📊 Learning Progress</h2>
            
            {/* Weekly Overview */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-800 mb-3">This Week</h3>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-xl font-bold text-blue-600">{childProgress?.lessons_completed || 0}</div>
                    <div className="text-xs text-slate-500">Lessons</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-emerald-600">{childProgress?.quizzes_passed || 0}</div>
                    <div className="text-xs text-slate-500">Quizzes</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-amber-600">{selectedChild?.total_xp || 0}</div>
                    <div className="text-xs text-slate-500">XP Earned</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Subject Progress */}
            {['Reading', 'Math', 'Writing', 'Science', 'Bible'].map((subject) => {
              const subjectData = childProgress?.subjects?.[subject.toLowerCase()] || { completed: 0, total: 10 };
              const percent = Math.round((subjectData.completed / Math.max(subjectData.total, 1)) * 100);
              const icons = { Reading: '📚', Math: '🔢', Writing: '✏️', Science: '🔬', Bible: '📖' };
              const colors = { Reading: 'emerald', Math: 'blue', Writing: 'amber', Science: 'purple', Bible: 'pink' };
              
              return (
                <Card key={subject}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex items-center gap-2">
                        <span className="text-lg">{icons[subject]}</span>
                        <span className="font-medium text-slate-800">{subject}</span>
                      </span>
                      <span className="text-sm text-slate-500">{subjectData.completed}/{subjectData.total}</span>
                    </div>
                    <Progress value={percent} className="h-2" />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-slate-400">{percent}% complete</span>
                      <button 
                        className="text-xs text-emerald-600 font-medium"
                        onClick={() => navigate(`/ai-curriculum/${selectedChild.id}/${subject.toLowerCase()}`)}
                      >
                        Continue →
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Streak Info */}
            <Card className="bg-gradient-to-r from-orange-50 to-amber-50">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Flame className="w-4 h-4 text-orange-500" />
                  Streak Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-orange-500">{streak?.current_streak || 0}</p>
                    <p className="text-xs text-slate-500">Current Streak</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-500">{streak?.longest_streak || 0}</p>
                    <p className="text-xs text-slate-500">Best Streak</p>
                  </div>
                </div>
                {streak?.current_streak >= 7 && (
                  <div className="mt-3 text-center text-xs text-orange-600 bg-orange-100 rounded-full py-1">
                    🔥 {streak.current_streak} day streak! Keep it going!
                  </div>
                )}
              </CardContent>
            </Card>

            {/* View Full Report Button */}
            <Button 
              className="w-full" 
              onClick={() => navigate(`/progress-report/${selectedChild.id}`)}
            >
              <FileText className="w-4 h-4 mr-2" />
              View Full Progress Report
            </Button>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-800">⚙️ Settings</h2>
            
            {/* Account Section */}
            <Card>
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                      {user?.name?.charAt(0) || 'P'}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{user?.name || 'Parent'}</p>
                      <p className="text-sm text-slate-500">{user?.email}</p>
                    </div>
                  </div>
                </div>
                <button 
                  className="w-full p-4 flex items-center justify-between border-b hover:bg-slate-50 active:bg-slate-100"
                  onClick={() => navigate('/settings')}
                >
                  <span className="flex items-center gap-3">
                    <User className="w-5 h-5 text-slate-400" />
                    <span>Account Settings</span>
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
                <button 
                  className="w-full p-4 flex items-center justify-between border-b hover:bg-slate-50 active:bg-slate-100"
                  onClick={() => navigate('/dashboard')}
                >
                  <span className="flex items-center gap-3">
                    <BarChart3 className="w-5 h-5 text-slate-400" />
                    <span>Full Dashboard</span>
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
                <button 
                  className="w-full p-4 flex items-center justify-between border-b hover:bg-slate-50 active:bg-slate-100"
                  onClick={() => navigate('/pricing')}
                >
                  <span className="flex items-center gap-3">
                    <Star className="w-5 h-5 text-amber-500" />
                    <span>Subscription</span>
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
                <button 
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 active:bg-slate-100"
                  onClick={() => navigate('/help')}
                >
                  <span className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-slate-400" />
                    <span>Help Center</span>
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              </CardContent>
            </Card>

            {/* Children Section */}
            <Card>
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-sm">My Children</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {children.map((child, idx) => (
                  <button 
                    key={child.id}
                    className={`w-full p-3 flex items-center justify-between hover:bg-slate-50 active:bg-slate-100 ${idx < children.length - 1 ? 'border-b border-slate-100' : ''}`}
                    onClick={() => navigate(`/child/${child.id}/edit`)}
                  >
                    <span className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-medium">
                        {child.name?.charAt(0)}
                      </div>
                      <span className="text-sm">{child.name}</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                ))}
                <button 
                  className="w-full p-3 flex items-center justify-center gap-2 text-emerald-600 hover:bg-emerald-50 active:bg-emerald-100"
                  onClick={() => navigate('/child/new')}
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">Add Child</span>
                </button>
              </CardContent>
            </Card>
            
            {/* Logout */}
            <Button 
              variant="outline" 
              className="w-full text-red-500 border-red-200 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
            
            <p className="text-center text-xs text-slate-400 mt-4">
              Zazu v1.0.0
            </p>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2 flex justify-around">
        {[
          { id: 'home', icon: Home, label: 'Home' },
          { id: 'progress', icon: BarChart3, label: 'Progress' },
          { id: 'settings', icon: Settings, label: 'Settings' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col items-center py-2 px-6 rounded-xl transition-colors ${
              activeTab === tab.id 
                ? 'text-emerald-600 bg-emerald-50' 
                : 'text-slate-400'
            }`}
          >
            <tab.icon className="w-6 h-6" />
            <span className="text-xs mt-1 font-medium">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MobileParentApp;
